import React, { useMemo } from "react";
import KC_Table from "../../../../../designsystem/KC_Table/KC_Table";
import { useDispatch, useSelector } from "react-redux";
import { type RootStateI } from "../../../../../redux";
import { tt } from "../../../../../utils/i18n/i18n";
import TSUtils from "../../../../../utils/helpers/TSUtils";
import KC_Button, {
  KC_ButtonSize
} from "../../../../../designsystem/KC_Button/KC_Button";
import { KC_Weights } from "../../../../../designsystem/ds_commons";
import KC_Heading3 from "../../../../../designsystem/typography/KC-Heading/KC_Heading3";
import { type POSAOrderItem } from "../../../../../redux/saas/reducer";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import {
  usePOSAOrderIsRecomputingSelector,
  usePOSAPricesSelector,
  usePOSATypesSelector
} from "../../../../../redux/actions/wheel";
import KC_Icon_Button from "../../../../../designsystem/KC_Button/KC_Icon_Button";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  changePOSAOrderItemQuantityThunk,
  removePOSAOrderItem
} from "../../../../../redux/saas/thunks";
import {
  distribution,
  posaDistributionQuantityInfo
} from "../../../../../utils/routes/routes";
import POSAOrderBottomButtons from "./POSAOrderBottomButtons";
import {
  Currencies,
  DEFAULT_CURRENCY_SUB_UNITS
} from "@tootsweet/model/enums/Currencies";
import { buildPathWithTrackingId } from "../../../../../utils/routing/routingUtils";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import NiceModal from "@ebay/nice-modal-react";
import PostPaymentModal from "../../../../../components/modal/ConfirmPayment/PostPaymentModal";

interface Props {
  gotoPath?: (path: string) => void;
  isModal?: boolean;
}

export default function POSAList(props: Props) {
  const dispatch = useDispatch();
  const allPOSATypes = usePOSATypesSelector();
  const allPOSAPrices = usePOSAPricesSelector();
  const posaOrderIsRecomputing = usePOSAOrderIsRecomputingSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const currentPOSAOrder = useSelector(
    (state: RootStateI) => state.saas.currentPOSAOrder
  );

  const goBack = () => {
    if (props.gotoPath != null) {
      props.gotoPath(distribution);
    }
  };

  const onChangeQty = (item: POSAOrderItem, index: number, newQty: number) => {
    dispatch(changePOSAOrderItemQuantityThunk(index, newQty));
  };

  const onChangeQtyWithPriceList = (
    item: POSAOrderItem,
    index: number,
    newQty: number
  ) => {
    dispatch(changePOSAOrderItemQuantityThunk(index, newQty));
  };

  const displayPOSAName = (posa: POSAOrderItem) => {
    const posaType = allPOSATypes.find((p) => p._id === posa.posaTypeId);
    if (posaType == null) {
      return tt("posa_list_unknown");
    }
    if (posaType && posaType.hasPriceList) {
      return `${posaType.name} *`;
    }
    return posaType.name;
  };

  const isItemHasPriceList = (posa: POSAOrderItem): boolean => {
    const posaType = allPOSATypes.find((p) => p._id === posa.posaTypeId);
    if (posaType == null) {
      return false;
    }
    return posaType.hasPriceList;
  };

  const handleRemoveItem = (e: any, index: number) => {
    e.stopPropagation();
    dispatch(removePOSAOrderItem(index));
  };

  const hasInvalidQuantities = useMemo(() => {
    return currentPOSAOrder.items?.some((item) => !item.qty) ?? false;
  }, [currentPOSAOrder.items]);

  return (
    <div className="d-flex flex-column h-100 st-gap" id={"posa-list-container"}>
      <div className="p-4">
        <KC_Heading3 text={tt("posa_list_selected_supports")} />
      </div>
      <div className="p-4">
        <KC_Table
          className="border rounded"
          keyPrefix={""}
          headers={
            <tr>
              <th>
                <KC_UIText weight={KC_Weights.md} text={tt("posa_list_type")} />
              </th>
              <th>
                <KC_UIText
                  weight={KC_Weights.md}
                  text={tt("posa_list_quantity")}
                />
              </th>
              <th>
                <KC_UIText
                  weight={KC_Weights.md}
                  text={tt("posa_list_unit_price")}
                />
              </th>
              <th>
                <KC_UIText
                  weight={KC_Weights.md}
                  text={tt("posa_list_total_ht")}
                />
              </th>
              <th>
                <KC_UIText
                  weight={KC_Weights.md}
                  text={tt("posa_list_total_ttc")}
                />
              </th>
              <th></th>
            </tr>
          }
          children={
            <>
              {currentPOSAOrder.items?.map((item, index) => {
                const posaType = allPOSATypes.find(
                  (p) => p._id === item.posaTypeId
                );

                if (posaType == null) {
                  return;
                }

                const pricesList = allPOSAPrices.find(
                  (price) => price._id === posaType.helloPrintSlug
                );

                const hasPriceList =
                  isItemHasPriceList(item) && pricesList != null;

                return (
                  <tr key={item.posaTypeId} id={item.posaTypeId}>
                    <td className="text-left">
                      <div className="w-100 d-flex gap-sm align-items-center justify-content-start">
                        <KC_UIText
                          weight={KC_Weights.reg}
                          text={displayPOSAName(item)}
                        />
                      </div>
                    </td>
                    <td>
                      {hasPriceList ? (
                        <div
                          className="kc-input-wrapper"
                          style={{ width: 180 }}
                        >
                          <select
                            className="kc_input"
                            id={`kc-${item.posaTypeId}-quantity-select`}
                            onChange={(e) => {
                              onChangeQtyWithPriceList(
                                item,
                                index,
                                parseInt(e.target.value)
                              );
                            }}
                            value={item.qty}
                          >
                            <option>{tt("mandatory")}</option>
                            {Object.keys(pricesList.prices).map(
                              (qty: any, idx: number) => (
                                <option
                                  key={qty + idx + item.posaTypeId}
                                  value={qty}
                                >
                                  {tt("posa_list_units", {
                                    qty,
                                    s: qty > 1 ? "s" : "",
                                    price: TSUtils.formatAmount(
                                      pricesList.prices[qty],
                                      DEFAULT_CURRENCY_SUB_UNITS,
                                      false,
                                      Currencies.CURRENCY_EUR
                                    )
                                  })}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      ) : (
                        <div
                          className="kc-input-wrapper"
                          style={{ width: 180 }}
                        >
                          <input
                            id={`kc-${item.posaTypeId}-quantity-input`}
                            className="kc_input"
                            type="number"
                            step={1}
                            value={item.qty}
                            onChange={(e) => {
                              onChangeQty(item, index, e.target.valueAsNumber);
                            }}
                            style={{ padding: "0 0 0 16px" }}
                          />
                        </div>
                      )}
                    </td>
                    <td>
                      {posaOrderIsRecomputing && (
                        <div className="loader-spinner"></div>
                      )}
                      {!posaOrderIsRecomputing && hasPriceList
                        ? ""
                        : TSUtils.formatAmount(
                            item.unitPrice,
                            DEFAULT_CURRENCY_SUB_UNITS,
                            false,
                            Currencies.CURRENCY_EUR
                          )}
                    </td>
                    <td>
                      {posaOrderIsRecomputing && (
                        <div className="loader-spinner"></div>
                      )}
                      {!posaOrderIsRecomputing &&
                        TSUtils.formatAmount(
                          item.priceHT,
                          DEFAULT_CURRENCY_SUB_UNITS,
                          false,
                          Currencies.CURRENCY_EUR
                        )}
                    </td>
                    <td>
                      {posaOrderIsRecomputing && (
                        <div className="loader-spinner"></div>
                      )}
                      {!posaOrderIsRecomputing &&
                        TSUtils.formatAmount(
                          item.priceTTC,
                          DEFAULT_CURRENCY_SUB_UNITS,
                          false,
                          Currencies.CURRENCY_EUR
                        )}
                    </td>
                    <td>
                      <KC_Icon_Button
                        type={"button"}
                        isInverted={true}
                        size={KC_ButtonSize.Medium}
                        icon={faTrash}
                        onClick={(e) => handleRemoveItem(e, index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </>
          }
        />
      </div>

      <div className="d-flex justify-content-end align-items-center p-4">
        <KC_UIText
          weight={KC_Weights.bold}
          text={tt("posa_list_total_ttc_label")}
          className="text-secondary mr-2"
        />
        <KC_Heading3
          id="total-ttc"
          text={TSUtils.formatAmount(
            currentPOSAOrder.totalPriceTTC,
            DEFAULT_CURRENCY_SUB_UNITS,
            false,
            Currencies.CURRENCY_EUR
          )}
          primary={true}
        />
      </div>

      {!props.isModal && (
        <div
          id="posa-footer"
          className="d-flex justify-content-between align-items-center border-top p-4 posa-footer"
        >
          <POSAOrderBottomButtons
            leftButton={
              <KC_Button
                isInverted={true}
                size={KC_ButtonSize.Large}
                text={tt("posa_list_previous")}
                onClick={goBack}
                className="d-flex"
              />
            }
            rightButton={
              <KC_Button
                id="posa-list-next-btn"
                isPrimary={true}
                size={KC_ButtonSize.Large}
                text={tt("posa_list_next")}
                onClick={(e) => {
                  if (hasInvalidQuantities) {
                    e.preventDefault();
                    e.stopPropagation();
                    void NiceModal.show(PostPaymentModal, {
                      headerText: tt("error"),
                      bodyText: tt("posa_list_quantity_required")
                    });
                    return;
                  }
                  if (props.gotoPath != null) {
                    props.gotoPath(
                      buildPathWithTrackingId(
                        posaDistributionQuantityInfo,
                        accountToDisplay?.trackingID
                      )
                    );
                  }
                }}
                className="d-flex"
              />
            }
          />
        </div>
      )}
    </div>
  );
}
